/**
 * AboutSection Component
 * Displays prospect biographical information, teams, and contact actions
 * Mirrors mobile app About tab functionality
 */

import type { ProspectResponse, TeamInfo } from "../../../../../types/api";
import {
  formatPhoneNumber,
  formatDateForDisplay,
} from "../../../../../utils/formatters";
import { PhoneIcon } from "../../../../../assets/PhoneIcon";
import { MessageIcon } from "../../../../../assets/MessageIcon";
import { EmailIcon } from "../../../../../assets/EmailIcon";
import { XIcon } from "../../../../../assets/XIcon";
import { InstagramIcon } from "../../../../../assets/InstagramIcon";
import { EditIcon } from "../../../../../assets/EditIcon";
import { TrashIcon } from "../../../../../assets/TrashIcon";
import { formatPlayerLevel } from "../../../../../constants/playerLevels";
import "./AboutSection.css";

interface AboutSectionProps {
  prospect: ProspectResponse;
  onJerseyClick?: (team: TeamInfo) => void;
  onRemoveTeam?: (teamId: string) => void;
  onAddTeam?: () => void;
}

/**
 * Parses Twitter URL from various formats (@username, username, or full URL)
 * Returns full URL or 'N/A' if empty
 */
function parseTwitterUrl(twitterUrl: string | null | undefined): string {
  if (!twitterUrl) return "N/A";
  const trimmed = twitterUrl.trim();

  // Already a full URL
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Handle @username or username
  const username = trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  return `https://twitter.com/${username}`;
}

/**
 * Parses Instagram URL from various formats (@username, username, or full URL)
 * Returns full URL or 'N/A' if empty
 */
function parseInstagramUrl(instagramUrl: string | null | undefined): string {
  if (!instagramUrl) return "N/A";
  const trimmed = instagramUrl.trim();

  // Already a full URL
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Handle @username or username
  const username = trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  return `https://instagram.com/${username}`;
}

export function AboutSection({ prospect, onJerseyClick, onRemoveTeam, onAddTeam }: AboutSectionProps) {
  return (
    <div className="about-section">
      {/* Teams Section */}
      <div className="about-section__subsection">
        <div className="about-section__subsection-header">
          <h4 className="about-section__subsection-title">Teams</h4>
          {onAddTeam && (
            <button
              className="about-section__add-team-button"
              onClick={onAddTeam}
              aria-label="Add to team"
            >
              <span className="about-section__add-team-button-icon">+</span>
              <span>Add Team</span>
            </button>
          )}
        </div>
        <div className="about-section__teams-list">
          {prospect.teams && prospect.teams.length > 0 && (
            prospect.teams.map((team) => (
              <div key={team.id} className="about-section__team-card">
                <span className="about-section__team-name">{team.name}</span>
                <div className="about-section__team-actions">
                  <button
                    className="about-section__team-jersey"
                    onClick={() => onJerseyClick?.(team)}
                    aria-label={`Edit jersey number for ${team.name}`}
                  >
                    <span className="about-section__team-jersey-number">
                      #{team.jerseyNumber ?? "?"}
                    </span>
                    <EditIcon className="about-section__team-jersey-icon" />
                  </button>
                  {onRemoveTeam && (
                    <button
                      className="about-section__team-remove"
                      onClick={() => onRemoveTeam(team.id)}
                      aria-label={`Remove from ${team.name}`}
                    >
                      <TrashIcon className="about-section__team-remove-icon" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Contact Action Buttons */}
      {(prospect.phone ||
        prospect.email ||
        prospect.about?.twitterUrl ||
        prospect.about?.instagramUrl) && (
        <div className="about-section__contact-actions">
          {prospect.phone && (
            <>
              <a
                href={`tel:${prospect.phone}`}
                className="about-section__contact-button"
                title="Call"
                aria-label="Call prospect"
              >
                <PhoneIcon className="about-section__contact-icon" />
              </a>
              <a
                href={`sms:${prospect.phone}`}
                className="about-section__contact-button"
                title="Text"
                aria-label="Text prospect"
              >
                <MessageIcon className="about-section__contact-icon" />
              </a>
            </>
          )}
          {prospect.email && (
            <a
              href={`mailto:${prospect.email}`}
              className="about-section__contact-button"
              title="Email"
              aria-label="Email prospect"
            >
              <EmailIcon className="about-section__contact-icon" />
            </a>
          )}
          {prospect.about?.twitterUrl && (
            <a
              href={parseTwitterUrl(prospect.about.twitterUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="about-section__contact-button"
              title="Twitter/X"
              aria-label="View Twitter profile"
            >
              <XIcon className="about-section__contact-icon" />
            </a>
          )}
          {prospect.about?.instagramUrl && (
            <a
              href={parseInstagramUrl(prospect.about.instagramUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="about-section__contact-button"
              title="Instagram"
              aria-label="View Instagram profile"
            >
              <InstagramIcon className="about-section__contact-icon" />
            </a>
          )}
        </div>
      )}

      {/* Main Information Card - All 15 Fields */}
      <div className="about-section__content">
        {/* 1. Phone */}
        <div className="about-section__item">
          <span className="about-section__label">Phone</span>
          <span className="about-section__value">
            {prospect.phone || "N/A"}
          </span>
        </div>

        {/* 2. Email */}
        <div className="about-section__item">
          <span className="about-section__label">Email</span>
          <span className="about-section__value">
            {prospect.email || "N/A"}
          </span>
        </div>

        {/* 3. X (Twitter) */}
        <div className="about-section__item">
          <span className="about-section__label">X</span>
          <span className="about-section__value">
            {prospect.about?.twitterUrl || "N/A"}
          </span>
        </div>

        {/* 4. Instagram */}
        <div className="about-section__item">
          <span className="about-section__label">Instagram</span>
          <span className="about-section__value">
            {prospect.about?.instagramUrl || "N/A"}
          </span>
        </div>

        {/* 5. Player Level */}
        <div className="about-section__item">
          <span className="about-section__label">Player Level</span>
          <span className="about-section__value">
            {/* Top-level first, `about` as the fallback while the old column lingers. `??`
                rather than `||`, since High School is the ordinal 0. */}
            {formatPlayerLevel(
              prospect.playerLevel ?? prospect.about?.playerLevel,
            ) ?? "N/A"}
          </span>
        </div>

        {/* 6. City / State */}
        <div className="about-section__item">
          <span className="about-section__label">City / State</span>
          <span className="about-section__value">
            {prospect.about?.city && prospect.about?.state
              ? `${prospect.about.city}, ${prospect.about.state}`
              : "N/A"}
          </span>
        </div>

        {/* 7. Birth Date */}
        <div className="about-section__item">
          <span className="about-section__label">Birth Date</span>
          <span className="about-section__value">
            {prospect.about?.birthDate
              ? formatDateForDisplay(prospect.about.birthDate)
              : "N/A"}
          </span>
        </div>

        {/* 8. Visit Date */}
        <div className="about-section__item">
          <span className="about-section__label">Visit Date</span>
          <span className="about-section__value">
            {prospect.about?.visitDate
              ? formatDateForDisplay(prospect.about.visitDate)
              : "N/A"}
          </span>
        </div>

        {/* 9. Address */}
        <div className="about-section__item">
          <span className="about-section__label">Address</span>
          <span className="about-section__value">
            {prospect.about?.address || "N/A"}
          </span>
        </div>

        {/* 9b. Zip code */}
        <div className="about-section__item">
          <span className="about-section__label">Zip Code</span>
          <span className="about-section__value">
            {prospect.about?.zipCode || "N/A"}
          </span>
        </div>

        {/* 10. Parent name */}
        <div className="about-section__item">
          <span className="about-section__label">Parent name</span>
          <span className="about-section__value">
            {prospect.about?.parentName || "N/A"}
          </span>
        </div>

        {/* 11. Parent phone number */}
        <div className="about-section__item">
          <span className="about-section__label">Parent phone number</span>
          <span className="about-section__value">
            {prospect.about?.parentPhone
              ? formatPhoneNumber(prospect.about.parentPhone)
              : "N/A"}
          </span>
        </div>

        {/* 12. Coach name */}
        <div className="about-section__item">
          <span className="about-section__label">Coach name</span>
          <span className="about-section__value">
            {prospect.about?.coachName || "N/A"}
          </span>
        </div>

        {/* 13. Coach phone number */}
        <div className="about-section__item">
          <span className="about-section__label">Coach phone number</span>
          <span className="about-section__value">
            {prospect.about?.coachPhone
              ? formatPhoneNumber(prospect.about.coachPhone)
              : "N/A"}
          </span>
        </div>

        {/* 14. Advisor name */}
        <div className="about-section__item">
          <span className="about-section__label">Advisor name</span>
          <span className="about-section__value">
            {prospect.about?.advisorName || "N/A"}
          </span>
        </div>

        {/* 15. Advisor phone number */}
        <div className="about-section__item">
          <span className="about-section__label">Advisor phone number</span>
          <span className="about-section__value">
            {prospect.about?.advisorPhone
              ? formatPhoneNumber(prospect.about.advisorPhone)
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
}
